import { create } from 'zustand'
import axios from 'axios'
import { env } from '@/lib/env'

export type AdminRole = 'superAdmin' | 'blogger' | 'broadcastOfficer' | 'payoutOfficer' | 't3Onboarding' | 'COURSE' | 'collegeAdmin' | 'caller' | 'marketing' | 'optionForm'

export type User = {
  userId: string
  email: string
  jwtToken: string
  firebaseCustomToken: string
  role?: AdminRole
} | null

type AuthState = {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
  clearError: () => void
  error: string | null
  isInitialized: boolean
  initializeAuth: () => void
}

const AUTH_KEYS = ['token', 'adminEmail', 'user'] as const

// localStorage is the SINGLE source of truth for auth. It is synchronous and
// survives tab/browser close, so the session is always available immediately on
// load and the user stays logged in until they explicitly log out (or the token
// is rejected with a 401). There is no sessionStorage mirror — one store, no race.
function persistAuth(token: string, email: string, userData: unknown) {
  localStorage.setItem('token', token)
  localStorage.setItem('adminEmail', email)
  localStorage.setItem('user', JSON.stringify(userData))
}

function clearAuth() {
  for (const key of AUTH_KEYS) localStorage.removeItem(key)
}

// Read the persisted session synchronously so the store starts in the correct
// state on the very first render (no logged-out flash for an existing session).
function readStoredAuth(): { user: User; token: string | null } {
  try {
    const token = localStorage.getItem('token')
    const rawUser = localStorage.getItem('user')
    if (token && rawUser) {
      const parsed = JSON.parse(rawUser)
      return { user: { ...parsed, role: parsed.role || 'superAdmin' }, token }
    }
  } catch {
    // Corrupt stored user — fall through to logged-out state.
  }
  return { user: null, token: null }
}

const initialAuth = readStoredAuth()

export const useAuthStore = create<AuthState>((set) => ({
  user: initialAuth.user,
  token: initialAuth.token,
  loading: false,
  // The session is read synchronously from localStorage above, so the router is
  // ready from the very first render — no skeleton/login flash, no extra refresh.
  isInitialized: true,
  error: null,
  login: async (email: string, password: string) => {
    set({ loading: true })
    try {
      const res = await axios.post(
        `${env.BACKEND_URL}/api/auth/adminSignin`,
        {
          email: email,
          password: password,
        },
        {
          headers: {
            Accept: 'application/json',
          },
        }
      )
      // If no role is provided, default to superAdmin
      const userData = {
        ...res.data,
        email: email,
        role: res.data.role || 'superAdmin',
      }

      persistAuth(res.data.jwtToken, email, userData)
      set({ token: res.data.jwtToken, loading: false, user: userData })
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.message ?? err.message
        : 'Random Error'
      set({ loading: false, error: message })
    }
  },
  logout: () => {
    set({ user: null, token: null, loading: false, error: null })
    clearAuth()
    // Clear navigation stack on logout
    sessionStorage.removeItem('navigation-storage')
  },
  initializeAuth: () => {
    // State was already resolved synchronously from localStorage at module load.
    // This just re-confirms it (and recovers if storage changed in another tab).
    const { user, token } = readStoredAuth()
    set({ user, token, isInitialized: true })
  },
  clearError: () => {
    set({ error: null })
  },
}))
